import React from 'react';
import { Link } from 'react-router-dom';

const CareGuide = () => {
  return (
    <div style={{ background: 'var(--background)', minHeight: '100vh', paddingTop: '80px' }}>
      <div className="container" style={{ paddingBottom: '80px' }}>
        
        {/* Hero Section */}
        <header style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            borderRadius: '50px',
            background: 'hsl(200, 85%, 25%, 0.1)',
            color: 'var(--primary)',
            marginBottom: '1rem'
          }}>
            <span style={{ fontSize: '1.5rem' }}>🐠</span>
            <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>Home Marine Aquarium · Beginner–Intermediate</span>
          </div>
          <h1 className="font-display" style={{
            fontSize: 'clamp(3rem, 8vw, 5rem)',
            fontWeight: 'bold',
            color: 'var(--foreground)',
            marginBottom: '1rem'
          }}>
            Marine Aquarium Fish Care Guide
          </h1>
          <p style={{
            color: 'var(--muted-foreground)',
            maxWidth: '42rem',
            margin: '0 auto',
            fontSize: '1.25rem',
            lineHeight: '1.6'
          }}>
            Set up, stock, and maintain a stable saltwater tank at home without constant fish losses or algae disasters.
          </p>
        </header>

        {/* Quick Overview & Checklist Grid */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', 
          gap: '2rem', 
          marginBottom: '4rem' 
        }}>
          
          {/* Quick Overview */}
          <div className="reef-card" style={{ padding: '2rem' }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              color: 'var(--ocean-deep)',
              marginBottom: '1rem'
            }}>
              Quick Overview
            </h2>
            <p style={{ marginBottom: '1.5rem', color: 'var(--muted-foreground)' }}>
              <strong>Goal:</strong> Keep marine fish healthy in a stable home tank.
            </p>
            <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <li style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                <span style={{ color: 'var(--ocean-light)', fontSize: '1rem', marginTop: '0.2rem' }}>●</span>
                <span><strong>Tank size:</strong> Aim for <strong>120–180 L</strong> minimum for beginners. Bigger = more stable.</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                <span style={{ color: 'var(--ocean-light)', fontSize: '1rem', marginTop: '0.2rem' }}>●</span>
                <span><strong>Core focus:</strong> Stable <em>water parameters</em> and <em>patience</em> with stocking.</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                <span style={{ color: 'var(--ocean-light)', fontSize: '1rem', marginTop: '0.2rem' }}>●</span>
                <span><strong>Timeline:</strong> Expect <strong>2–6 weeks</strong> to fully cycle before adding fish.</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                <span style={{ color: 'var(--ocean-light)', fontSize: '1rem', marginTop: '0.2rem' }}>●</span>
                <span><strong>Biggest killers:</strong> Rushing cycling, overfeeding, bad tap water, and mixing aggressive fish.</span>
              </li>
            </ul>
          </div>

          {/* Beginner Checklist */}
          <div className="reef-card" style={{ padding: '2rem' }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              color: 'var(--ocean-deep)',
              marginBottom: '1rem'
            }}>
              Beginner Checklist
            </h2>
            <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {[
                'Tank 120–180 L with stand',
                'RO/DI water + quality marine salt',
                'Protein skimmer & good circulation pumps',
                'Heater + reliable thermometer',
                'Live rock / dry rock, sand bed',
                'Test kits: ammonia, nitrite, nitrate, pH, salinity',
                'Quarantine tank for new arrivals'
              ].map((item, index) => (
                <li key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ color: 'var(--ocean-light)', fontSize: '1.25rem' }}>✓</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Main Content Sections */}
        
        {/* 1. Tank & Equipment */}
        <section className="reef-card" style={{ padding: '2rem', marginBottom: '3rem' }} id="tank-equipment">
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: 'var(--ocean-deep)',
            marginBottom: '1.5rem'
          }}>
            1. Tank & Essential Equipment
          </h2>

          <h3 style={{ color: 'var(--ocean-mid)', marginBottom: '1rem' }}>Recommended Tank Size</h3>
          <p style={{ marginBottom: '2rem', lineHeight: '1.6' }}>
            For a first marine aquarium, use a tank of <strong>120–180 liters</strong>. Smaller tanks ("nano") look cute but
            are less forgiving—parameters swing faster and crashes happen easily.
          </p>

          <h3 style={{ color: 'var(--ocean-mid)', marginBottom: '1rem' }}>Core Equipment</h3>
          <ul style={{ lineHeight: '1.8', marginBottom: '2rem' }}>
            <li><strong>Protein Skimmer:</strong> Removes dissolved waste before it breaks down. Essential for cleaner water.</li>
            <li><strong>Heater & Thermometer:</strong> Keep temperature stable between <strong>24–26°C</strong>.</li>
            <li><strong>Return Pump / Powerheads:</strong> Provide strong, consistent flow; marine fish and bacteria need oxygenated moving water.</li>
            <li><strong>Filtration:</strong> Media for biological (bio-balls/ceramic), mechanical (filter pads), and optional chemical (carbon) filtration.</li>
            <li><strong>RO/DI Unit:</strong> Produces pure water; tap water usually introduces nutrients, metals, and algae fuel.</li>
            <li><strong>Marine Salt Mix:</strong> Use reputable brands specifically made for saltwater aquariums.</li>
            <li><strong>Lighting:</strong>
              <ul style={{ marginTop: '0.5rem' }}>
                <li><em>Fish-only:</em> Basic LED is fine.</li>
                <li><em>Reef (with corals):</em> High-quality, controllable LEDs designed for reef tanks.</li>
              </ul>
            </li>
          </ul>
        </section>

        {/* 2. Water Chemistry */}
        <section className="reef-card" style={{ padding: '2rem', marginBottom: '3rem' }} id="water-chemistry">
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: 'var(--ocean-deep)',
            marginBottom: '1rem'
          }}>
            2. Water Chemistry & Parameters
          </h2>
          <p style={{ 
            fontStyle: 'italic', 
            color: 'var(--ocean-mid)', 
            fontSize: '1.125rem', 
            marginBottom: '2rem' 
          }}>
            Water quality is the true "life support system" for your fish.
          </p>

          <h3 style={{ color: 'var(--ocean-mid)', marginBottom: '1rem' }}>Target Parameters</h3>
          <div style={{ overflowX: 'auto', marginBottom: '2rem' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)'
            }}>
              <thead>
                <tr style={{ background: 'var(--muted)' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', borderRight: '1px solid var(--border)' }}>Parameter</th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderRight: '1px solid var(--border)' }}>Ideal Range</th>
                  <th style={{ padding: '1rem', textAlign: 'left' }}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Salinity', '1.023–1.025 SG', 'Measure with a refractometer, not a cheap swing-arm hydrometer if possible.'],
                  ['Temperature', '24–26°C', 'Stability matters more than perfection.'],
                  ['pH', '8.1–8.4', 'Aeration and quality salt mix help keep pH stable.'],
                  ['Ammonia (NH₃)', '0 ppm', 'Any trace is toxic; indicates tank is not fully cycled or overloaded.'],
                  ['Nitrite (NO₂)', '0 ppm', 'Also toxic; should be zero in a mature tank.'],
                  ['Nitrate (NO₃)', '< 20 ppm (fish-only)', 'High levels stress fish and fuel algae; water changes reduce it.'],
                  ['Phosphate (PO₄)', '< 0.1 ppm', 'Primarily an algae driver; important if you plan corals.']
                ].map((row, index) => (
                  <tr key={index} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '0.75rem', fontWeight: 'bold', borderRight: '1px solid var(--border)' }}>{row[0]}</td>
                    <td style={{ padding: '0.75rem', borderRight: '1px solid var(--border)' }}>{row[1]}</td>
                    <td style={{ padding: '0.75rem' }}>{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 style={{ color: 'var(--ocean-mid)', marginBottom: '1rem' }}>Testing Routine</h3>
          <ul style={{ lineHeight: '1.8', marginBottom: '2rem' }}>
            <li><strong>Weekly:</strong> Test salinity, temperature, pH, nitrate.</li>
            <li><strong>More often in new tanks:</strong> Test ammonia and nitrite every few days until fully cycled.</li>
          </ul>

          <div style={{
            padding: '1.5rem',
            background: 'var(--coral)/10',
            borderLeft: '4px solid var(--coral)',
            borderRadius: 'var(--radius)',
            fontStyle: 'italic'
          }}>
            <strong>Rule of thumb:</strong> Fish rarely "just die." In most cases, unstable or poor water quality is behind it.
          </div>
        </section>

        {/* 3. Tank Cycling */}
        <section className="reef-card" style={{ padding: '2rem', marginBottom: '3rem' }} id="tank-cycling">
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: 'var(--ocean-deep)',
            marginBottom: '1.5rem'
          }}>
            3. Tank Cycling (Before Adding Fish)
          </h2>

          <h3 style={{ color: 'var(--ocean-mid)', marginBottom: '1rem' }}>What Is Cycling?</h3>
          <p style={{ marginBottom: '2rem', lineHeight: '1.6' }}>
            Cycling is the process of building up beneficial bacteria that convert toxic <strong>ammonia</strong> into
            <strong>nitrite</strong>, and then into less toxic <strong>nitrate</strong>. Until this biological filter is established,
            your tank is unsafe for fish.
          </p>

          <h3 style={{ color: 'var(--ocean-mid)', marginBottom: '1rem' }}>Basic Fishless Cycling Steps</h3>
          <ul style={{ lineHeight: '1.8' }}>
            <li>Fill tank with <strong>RO/DI water</strong> and mix in marine salt to correct salinity.</li>
            <li>Add live rock or bacterial starter culture to introduce beneficial bacteria.</li>
            <li>Add an ammonia source (fish food, pure ammonia, or Dr. Tim's ammonium chloride).</li>
            <li>Test water parameters daily and track the nitrogen cycle progression.</li>
            <li>Wait for ammonia and nitrite to spike, then drop to zero consistently.</li>
            <li>Only add fish when both ammonia and nitrite read 0 ppm for several days.</li>
          </ul>
        </section>

        {/* Browse Our Fish Collection CTA */}
        <div style={{
          textAlign: 'center',
          padding: '3rem 2rem',
          background: 'var(--gradient-ocean)',
          borderRadius: 'var(--radius)',
          color: 'white',
          marginTop: '4rem'
        }}>
          <h3 className="font-display" style={{
            fontSize: '2rem',
            fontWeight: '600',
            marginBottom: '1rem'
          }}>
            Ready to Start Your Marine Journey?
          </h3>
          <p style={{
            fontSize: '1.125rem',
            marginBottom: '2rem',
            opacity: 0.9
          }}>
            Browse our collection of carefully selected marine fish from the pristine waters of the Maldives.
            Each fish comes with detailed care information and compatibility guides.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/shop">
              <button className="btn-hero" style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                backdropFilter: 'blur(4px)'
              }}>
                🐠 Browse Fish Collection
              </button>
            </Link>
            <Link to="/contact">
              <button className="btn-ghost" style={{
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                e.target.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = 'white';
              }}
              >
                💬 Ask Our Experts
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CareGuide;